import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Shield } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { BrandLockup } from '@/components/BrandLockup'
import { MfaEnrollDialog } from '@/components/settings/MfaEnrollDialog'
import { useAuthStore } from '@/stores/auth-store'
import { resolveLoginRedirect } from '@/lib/redirect'

/**
 * Full-page wrapper for forced enrollment (e.g., for users where
 * mfa_required = TRUE and not yet enrolled). The dialog handles the actual
 * enroll flow; this page just provides framing + a "Sign out" escape hatch.
 *
 * R13 — self-gate. If unauthenticated → /login. If already enrolled → /app.
 */
export function MfaEnrollPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  // ProtectedRoute stashes the requested location before detouring here.
  const fromState = (location.state as { from?: { pathname: string; search?: string; hash?: string } } | null)?.from
  const enrolledTarget = resolveLoginRedirect({ fromState: fromState ?? null })
  const signOut = useAuthStore((s) => s.signOut)
  const [dialogOpen, setDialogOpen] = useState(true)

  useEffect(() => {
    if (!isAuthenticated) {
      void navigate('/login', { replace: true })
    }
    // Do NOT auto-navigate on mfaEnrolled — that races the dialog's
    // recovery-codes step (which shows ONCE and only here at enroll time).
    // The dialog's onEnrolled callback navigates away after the user
    // confirms they've saved the codes.
  }, [isAuthenticated, navigate])

  const handleSignOut = async () => {
    await signOut()
    void navigate('/login', { replace: true })
  }

  return (
    <div className="min-h-screen flex items-center justify-center hero-gradient p-4">
      <div className="w-full max-w-md">
        <div className="relative flex items-center justify-center mb-6">
          <BrandLockup iconSize={24} wordmarkClassName="text-2xl" />
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="absolute right-0 text-meta hover:underline"
          >
            Sign out
          </button>
        </div>

        <Card className="p-4">
          <CardHeader className="space-y-3">
            <div className="flex justify-center">
              <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                <Shield className="h-5 w-5 text-muted-foreground" />
              </div>
            </div>
            <CardTitle className="text-lg text-center">Set up two-factor authentication</CardTitle>
            <CardDescription className="text-center">
              Two-factor authentication is required for your account before you can continue.
            </CardDescription>
          </CardHeader>
          <CardContent className="flex justify-center pt-2">
            <Button type="button" onClick={() => setDialogOpen(true)}>
              Begin setup
            </Button>
          </CardContent>
        </Card>
      </div>

      <MfaEnrollDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onEnrolled={() => {
          void navigate(enrolledTarget, { replace: true })
        }}
      />
    </div>
  )
}
